import _ from 'lodash'
import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import PagePagination, {buildPageAsyncId} from './PagePagination'
import {getAsynCacheSelector} from "../../selectors";
import {createAsyncAction} from "../../index";

/**
 * PagePaginationWrapper
 * Học theo CustomLazyPaginationWrapper: tự dispatch action trang đầu nếu chưa có cache,
 * đọc thẳng qua redux selector (không qua AsyncByAction component), rồi mới quyết định
 * hiển thị gì dựa theo nội dung trả về (loading / rỗng / có data) trước khi giao cho
 * PagePagination render danh sách + thanh phân trang.
 *
 * Props:
 * - id: dùng làm base cho asyncId thay vì apiInfo.path (khi cần nhiều instance cùng apiInfo)
 * - apiInfo, query, values: giống LazyPagination
 * - defaultPage, defaultPerPage, pageParam, perPageParam: forward xuống PagePagination
 * - refreshKey: đổi giá trị -> reset về trang đầu (forward xuống PagePagination)
 * - alwaysVisible: true thì vẫn render (qua renderWrap) kể cả khi rỗng, thay vì return null
 * - isLoading: cờ loading từ bên ngoài (vd đang chờ điều kiện khác trước khi fetch)
 * - renderLoading: hiển thị khi đang tải trang đầu / chưa có cache
 * - renderEmpty: hiển thị khi trang đầu trả về rỗng và !alwaysVisible
 * - renderWrap(content, startAction, length): nếu truyền, TỰ QUYẾT ĐỊNH cách bọc/hiển thị
 *   nội dung (content) dựa vào length — dùng khi cha cần thêm điều kiện hiển thị riêng
 *   (vd chỉ hiện block khi length > 0 dù alwaysVisible=true, hoặc bọc thêm Card/Section)
 * - ...props: forward hết xuống PagePagination (renderItem, Header, Wrapper, v.v.)
 */
export default function PagePaginationWrapper({
    id,
    apiInfo,
    query,
    values,
    defaultPage = 1,
    defaultPerPage = 10,
    pageParam = 'page',
    perPageParam = 'per_page',
    refreshKey,
    alwaysVisible = false,
    isLoading: isLoadingProp = false,
    renderLoading,
    renderEmpty,
    renderWrap,
    isTable,
    ...props
}) {
    const recordId = id || apiInfo?.path
    const asyncId = buildPageAsyncId(recordId, defaultPage, defaultPerPage, query)

    const startAction = createAsyncAction({
        asyncId,
        query,
        // Cùng lý do như PagePagination.js: page/perPage phải nằm trong `values` để lọt vào
        // query string thực gửi lên BE (xem callApi.js) - `query` chỉ dùng để thay path-param.
        values: {...values, [pageParam]: defaultPage, [perPageParam]: defaultPerPage},
        apiInfo: {
            ...apiInfo,
            paramsFields: _.union(apiInfo?.paramsFields, [pageParam, perPageParam]),
        },
    })

    const dispatch = useDispatch()
    const {success, response} = useSelector(
        (state) => getAsynCacheSelector(state, startAction.asyncId),
        (left, right) => _.get(left, 'updated') === _.get(right, 'updated')
    )

    useEffect(() => {
        if (!isLoadingProp) dispatch(startAction)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asyncId, refreshKey])

    if ((isLoadingProp || !success) && renderLoading) {
        return renderLoading()
    }
    if (!success) return null

    const length = _.get(response, 'data.data.length', 0)
    if (!length && !alwaysVisible && !renderWrap) {
        return renderEmpty ? renderEmpty() : null
    }

    const content = (
        <PagePagination
            startAction={startAction}
            apiInfo={apiInfo}
            query={query}
            values={values}
            defaultPage={defaultPage}
            defaultPerPage={defaultPerPage}
            pageParam={pageParam}
            perPageParam={perPageParam}
            refreshKey={refreshKey}
            renderEmpty={renderEmpty}
            {...props}
        />
    )

    if (renderWrap) {
        return renderWrap(content, startAction, length)
    }

    return content
}
